import React, { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import FormatCurrency from 'components/Common/FormatCurrency'
import Clickable from 'components/Common/Clickable'
import { MONTHS } from 'constants/cruise'
import MonthTabButtonsGroup from 'components/OfferPage/TourV2/Departures/MonthTabs/MonthTabButtonsGroup'
import cn from 'clsx'

const Wrapper = styled.div`
  display: flex;

  ${mediaQueryUp.sm} {
    margin-top: ${rem(16)};
  }
`

export const BodyTextSmall = styled.div`
  font-size: ${rem(8)};
  line-height: ${rem(16)};

  ${mediaQueryUp.sm} {
    font-size: ${rem(12)};
  }
`
const Card = styled(Clickable)`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  text-align: -webkit-center;
  justify-content: center;
  align-items: center;
  width: ${rem(96)};
  height: ${rem(80)};
  border: 1px solid ${(props) => props.theme.palette.neutral.five};
  border-radius: ${props => props.theme.borderRadius.medium};
  background: ${(props) => props.theme.palette.neutral.eight};
  flex: none;

  &.card-selected{
    border-color: ${(props) => props.theme.palette.neutral.one};
  }
`

interface Destination {
  label: string;
  id: string;
  name: string;
}

const Tab = ({
  destination,
  departure,
  isSelected,
  onClick,
  departuresAmount,
  cachedDepartures,
}: {
  destination: Destination;
  departure: App.CruiseDepartureDetails;
  isSelected: boolean;
  onClick?: () => void;
  departuresAmount: number;
  cachedDepartures: { [cabinCategory: string]: App.CruisePriceDetails };
}) => {
  const lowestPrice = departure.lowestOverallPriceDetails

  const cachedValue = cachedDepartures[departure.id]
  if (cachedValue) {
    let idx = 0
    for (const k in cachedValue) {
      if (cachedValue[k].total < lowestPrice.total || idx === 0) lowestPrice.total = cachedValue[k].total
      idx++
    }
  }

  return (
    <Card onClick={onClick} key={destination.id} className={cn({ 'card-selected': isSelected })}>
      <BodyText variant="large" weight="bold">
        {destination.label}
      </BodyText>
      <BodyText
        variant="small"
        weight="bold"
        colour={isSelected ? 'neutral-one' : 'neutral-two'}
      >
        <FormatCurrency
          currency={lowestPrice?.currencyCode ?? 'AUD'}
          value={lowestPrice?.total ?? 0}
        />
        <BodyText weight="normal" variant="small">
          {departuresAmount > 1 ? `${departuresAmount} trips` : `${departuresAmount} trip`}
        </BodyText>
      </BodyText>
    </Card>
  )
}

interface Props {
  departureDate: { [date: string]: App.CruiseDepartureDetails[] };
  tab: any;
  setTab: (value: string) => void;
  year: string;
  onClick?: () => void;
  cachedDepartures: { [cabinCategory: string]: App.CruisePriceDetails };
}

function CruiseDepartureDatesTabs({
  departureDate,
  setTab,
  tab,
  year,
  cachedDepartures,
}: Props) {
  const monthTabsRef = useRef<HTMLDivElement>()

  const availableMonths = useMemo(
    () => Object.keys(departureDate).filter((i) => i.includes(year)),
    [departureDate, year],
  )
  const onClick = useCallback((newTab) => setTab(newTab), [setTab])

  return (
    <Wrapper>
      {availableMonths.length > 1 && <MonthTabButtonsGroup ref={monthTabsRef}>
        {MONTHS.map((destination) => {
          const containsMonth = availableMonths.find(
            (i) => i.slice(5) === destination.id,
          )
          if (!containsMonth) {
            return
          }
          const validDepartures = departureDate[containsMonth].filter(departure => departure.lowestOverallPriceDetails)
          if (!validDepartures.length) {
            return
          }
          const monthlyDepartures = departureDate[containsMonth].length
          const departure = validDepartures.sort((lowestPriceLeft, lowestPriceRight) => {
            return lowestPriceLeft.lowestOverallPriceDetails.total - lowestPriceRight.lowestOverallPriceDetails.total
          })[0]
          return (
            <Tab
              departure={departure}
              destination={destination}
              key={departure.id}
              isSelected={tab === containsMonth}
              onClick={() => onClick(containsMonth)}
              departuresAmount={monthlyDepartures}
              cachedDepartures={cachedDepartures}
            />
          )
        })}
      </MonthTabButtonsGroup>}
    </Wrapper>
  )
}

export default CruiseDepartureDatesTabs
